// src/Pages/ReleaseOverview.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';

import ReleaseModal from '../Components/ReleaseModal.js';
import ReleaseInfocard from '../Components/ReleaseInfocard';
import { useAudio } from '../contexts/AudioContext';

// icons / assets
import dollarIcon  from '../assets/icons/dollar.svg';
import duplicateIcon from '../assets/icons/duplicate.svg';
import infoIcon from '../assets/icons/info.svg';
import emptyArt    from '../assets/emptyart.jpg';

// Month names
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

// Calendar helpers
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dim = (y, m) => new Date(y, m + 1, 0).getDate();
const first = (y, m) => (new Date(y, m, 1).getDay() + 6) % 7; // 0..6, Monday first

// Tag colors (matching CalendarView)
const TAG_COLORS = {
  Album: '#31576c',
  EP: '#583e72',
  Single: '#555',
  'Impact Date': '#555',
  'Focus Single': '#f3b40b',
  'Re-Release': '#78be1e',
};

// Normalize tag values
const normalizeTag = (val) => {
  const t = String(val || '').trim().toLowerCase();
  switch (t) {
    case 'single': return 'Single';
    case 'focus single':
    case 'focussingle': return 'Focus Single';
    case 'impact date':
    case 'impact': return 'Impact Date';
    case 're-release':
    case 'rerelease':
    case 're release': return 'Re-Release';
    case 'album': return 'Album';
    case 'ep': return 'EP';
    default: return val || '';
  }
};

/* ─── helpers ────────────────────────────────────────── */
function isTaskOverdue(task, releaseDate) {
  if (!releaseDate)            return false;
  if (task.status === 'Completed') return false;
  if (!task.daysBefore)        return false;

  const due = new Date(releaseDate);
  due.setDate(due.getDate() - parseInt(task.daysBefore, 10));
  return due < new Date();
}
function isReleaseOverdue(release) {
  if (!release || !release.releaseDate || !release.releaseTasks) return false;
  return release.releaseTasks.some(t => isTaskOverdue(t, release.releaseDate));
}

/* ─── card component (memoized & lazy images) ────────────────── */
const ReleaseCardBase = ({ release, plannedBudget, artistNameMap, isPublicView = false }) => {
  const todayStr   = new Date().toISOString().split('T')[0];
  const isToday    = release.releaseDate?.slice(0,10) === todayStr;
  const overdue    = isReleaseOverdue(release);

  const formatted = release.releaseDate
    ? new Date(release.releaseDate)
        .toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
        })
        .replaceAll('/', '.')
    : 'No Release Date';

  // Handle both populated artists (objects with .name) and ID arrays
  const displayArtist = useMemo(() => {
    const arr = Array.isArray(release.artists) ? release.artists : [];
    return arr
      .map((a) => {
        if (a && typeof a === 'object') return a.name || a.title || '';
        const id = String(a || '');
        return artistNameMap[id] || '';
      })
      .filter(Boolean)
      .join(', ');
  }, [release.artists, artistNameMap]);

  const hasBudget = Number(plannedBudget) > 0;

  const cardContent = (
      <div
        style={{
          width:260,padding:20,margin:8,
          background:'#1e1e1e',borderRadius:10,color:'#fff',
          display:'flex',flexDirection:'column',justifyContent:'space-between',
          border:isToday?'2px solid #32CD32':'',
          boxShadow:isToday?'0 0 10px #32CD32':'',
          opacity:release.isArchived?0.5:1,transition:'opacity .3s',
          cursor: isPublicView ? 'default' : 'pointer',
        }}
      >
        <img
          src={release.coverArt || emptyArt}
          alt="Cover"
          loading="lazy"
          style={{ width:'100%',aspectRatio:'1/1',objectFit:'cover',borderRadius:10,marginBottom:12 }}
        />

        <div style={{ flexGrow:1 }}>
          <h3
            style={{
              margin: 0,
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: overdue ? '#ff4c4c' : '#fff',
            }}
          >
            {hasBudget && !isPublicView && (
              <img
                src={dollarIcon}
                alt="Budget set"
                style={{
                  width: 18,
                  height: 18,
                  filter:
                    'invert(68%) sepia(51%) saturate(734%) hue-rotate(63deg) brightness(97%) contrast(96%)',
                }}
              />
            )}
            {release.title}
            {overdue && <span style={{ fontSize: '0.9em' }}>⚠</span>}
          </h3>

          <p style={{
                margin:0,marginBottom:6,whiteSpace:'nowrap',
                overflow:'hidden',textOverflow:'ellipsis',color:'#ccc'
              }}>
            {displayArtist}
          </p>

          <p style={{ margin:0,color:'#aaa',fontSize:'.9rem' }}>{formatted}</p>
        </div>
      </div>
  );
  
  return isPublicView ? cardContent : (
    <Link to={`/release/${release._id}`} style={{ textDecoration:'none' }}>
      {cardContent}
    </Link>
  );
};
const ReleaseCard = React.memo(ReleaseCardBase, (a, b) => {
  return (
    a.release._id === b.release._id &&
    a.release.title === b.release.title &&
    a.release.coverArt === b.release.coverArt &&
    a.release.releaseDate === b.release.releaseDate &&
    a.release.isArchived === b.release.isArchived &&
    a.plannedBudget === b.plannedBudget &&
    a.isPublicView === b.isPublicView &&
    // artist names map identity (cheap enough)
    a.artistNameMap === b.artistNameMap
  );
});

/* ─── main page ──────────────────────────────────────── */
const ReleaseOverview = () => {
  /* check if public view */
  const [searchParams] = useSearchParams();
  const isPublicView = searchParams.get('public') === 'true';
  
  /* data */
  const [releases,    setReleases]    = useState([]);
  const [allArtists,  setAllArtists]  = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [releaseBudgets, setReleaseBudgets] = useState({});
  
  /* audio player */
  const { playTrack, currentTrack, togglePlayPause, isPlaying: globalIsPlaying } = useAudio();

  /* loading state */
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  /* filters / ui */
  const [fromDate, setFromDate]         = useState('');
  const [toDate,   setToDate]           = useState('');
  const [isFilterApplied,setIsFilterApplied] = useState(false);
  const [searchQuery, setSearchQuery]   = useState('');
  
  /* month filter */
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  
  /* view mode */
  const [viewMode, setViewMode] = useState('list'); // 'grid', 'list', or 'calendar'
  
  /* release counts */
  const [yearReleasesCount, setYearReleasesCount] = useState(0);
  const [loadingYearCount, setLoadingYearCount] = useState(false);
  
  /* public share */
  const [shareToken, setShareToken] = useState(null);
  const [showShareCopied, setShowShareCopied] = useState(false);

  /* modal */
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [editingDraft,     setEditingDraft]     = useState(null);   // null ⇒ add-mode
  
  /* infocard state */
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoRelease, setInfoRelease] = useState(null);

  /* artist id → name map for quick lookup */
  const artistNameMap = useMemo(() => {
    const map = {};
    for (const a of allArtists) {
      if (a && a._id) map[String(a._id)] = a.name || a.title || '';
    }
    return map;
  }, [allArtists]);

  /* ─ fetch on mount ─ */
  useEffect(() => {
    fetchArtists();
    fetchProjects();
    fetchBudgets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  /* ─ fetch releases when month changes or search query changes ─ */
  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      if (searchQuery && searchQuery.trim()) {
        // When searching, fetch all releases (for search across all months)
        fetchAllReleases();
      } else {
        // When not searching, fetch only current month
        fetchMonthReleases();
      }
    }, searchQuery && searchQuery.trim() ? 300 : 0); // 300ms debounce for search, immediate for month change

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, searchQuery]);
  
  /* ─ fetch year releases count when year changes ─ */
  useEffect(() => {
    const fetchYearReleases = async () => {
      const y = selectedMonth.getFullYear();
      const from = `${y}-01-01`;
      const to = `${y}-12-31`;
      setLoadingYearCount(true);
      try {
        const res = await api(`/api/calendar?from=${from}&to=${to}`);
        if (!res.ok) throw new Error('Failed to fetch year data');
        const data = await res.json();
        let count = 0;
        if (data.days) {
          for (const dayItems of Object.values(data.days)) {
            if (Array.isArray(dayItems)) {
              count += dayItems.filter(item => item.itemType === 'release').length;
            }
          }
        }
        setYearReleasesCount(count);
      } catch (e) {
        console.error('Failed to fetch year releases count:', e);
        setYearReleasesCount(0);
      } finally {
        setLoadingYearCount(false);
      }
    };
    
    fetchYearReleases();
  }, [selectedMonth.getFullYear()]);

  const fetchBudgets = async () => {
    try {
      const res  = await api('/api/releasebudgets');
      const list = await res.json();          // [{ releaseId, budget }, …]
      const map  = {};
      list.forEach(b => { map[b.releaseId] = b.budget; });
      setReleaseBudgets(map);
    } catch (err) {
      console.error(err);
    }
  };
  const fetchArtists = async () => {
    try{
      const res = await api('/api/artists');
      setAllArtists(await res.json());
    }catch(err){ console.error(err); }
  };
  const fetchProjects = async () => {
    try{
      const res = await api('/api/projects');
      setAllProjects(await res.json());
    }catch(err){ console.error(err); }
  };

  // Fetch all releases (for search)
  const fetchAllReleases = async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      params.set('limit', '1000'); // Get all releases
      params.set('page', '1');
      params.set('fields', 'title,coverArt,releaseDate,artists,releaseType,projectId,isArchived,isrc,upc,trackUpc,uploads,project');

      const res = await api(`/api/releases/paged?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { items } = await res.json();

      setReleases(items);
    } catch (e) {
      setError(e.message || 'Load error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch releases for the selected month
  const fetchMonthReleases = async () => {
    setLoading(true); setError('');
    try {
      const y = selectedMonth.getFullYear();
      const m = selectedMonth.getMonth();
      const from = `${y}-${String(m + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      const to = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      
      const res = await api(`/api/calendar?from=${from}&to=${to}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      
      // Extract releases from the days object
      const releasesArray = [];
      if (data.days) {
        for (const dayItems of Object.values(data.days)) {
          if (Array.isArray(dayItems)) {
            dayItems.forEach(item => {
              if (item.itemType === 'release') {
                releasesArray.push(item);
              }
            });
          }
        }
      }
      
      setReleases(releasesArray);
    } catch (e) {
      setError(e.message || 'Load error');
    } finally {
      setLoading(false);
    }
  };

  /* ─ handlers ─ */
  const handleAddRelease  = () => { setEditingDraft(null); setShowReleaseModal(true); };
  const handleEditRelease = rel => { setEditingDraft(rel); setShowReleaseModal(true); };
  
  // Open infocard for a release
  const openInfoFor = async (releaseId) => {
    try {
      const r = await api(`/api/releases/${releaseId}`);
      if (!r.ok) throw new Error(`Fetch release failed (${r.status})`);
      const full = await r.json();
      setInfoRelease(full);
      setInfoOpen(true);
    } catch (e) {
      alert(e.message || 'Could not load release info');
    }
  };
  
  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    if (!text || text === '—') return;
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here if desired
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };
  
  // Handle share button - copy public link
  const handleShare = () => {
    const publicUrl = `${window.location.origin}/release-overview?public=true`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setShowShareCopied(true);
      setTimeout(() => setShowShareCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy link:', err);
      alert('Failed to copy link to clipboard');
    });
  };
  
  // Handle audio playback
  const handlePlayAudio = async (rel) => {
    const mp3Key = rel.uploads?.mp3Master;
    if (!mp3Key) return;

    // If this track is already playing, toggle play/pause
    if (currentTrack?.releaseId === rel._id) {
      togglePlayPause();
      return;
    }

    try {
      let mp3Url = null;

      // If it's already a full URL, use it directly
      if (/^https?:\/\//i.test(mp3Key)) {
        mp3Url = mp3Key;
      } else {
        // Otherwise, get a signed URL from the backend
        const res = await api(`/api/assets/sign-get?key=${encodeURIComponent(mp3Key)}`);
        if (!res.ok) throw new Error('Failed to get signed URL');
        const { url } = await res.json();
        mp3Url = url;
      }

      // Get artist name for display
      const arr = Array.isArray(rel.artists) ? rel.artists : [];
      const artistName = arr
        .map((a) => {
          if (a && typeof a === 'object') return a.name || a.title || '';
          const id = String(a || '');
          return artistNameMap[id] || '';
        })
        .filter(Boolean)
        .join(', ');

      playTrack({
        audioUrl: mp3Url,
        trackTitle: rel.title || 'Unknown Track',
        artistName: artistName || 'Unknown Artist',
        coverArt: rel.coverArt || emptyArt,
        releaseId: rel._id,
      });
    } catch (err) {
      console.error('Error playing audio:', err);
      alert('Could not play audio. Please check if the file exists.');
    }
  };

  const saveRelease = async draft => {
    try{
      if (editingDraft) {
        await api(`/api/releases/${editingDraft._id}`,{
          method:'PUT',headers:{'Content-Type':'application/json'},
          body:JSON.stringify(draft)
        });
      } else {
        await api('/api/releases',{
          method:'POST',headers:{'Content-Type':'application/json'},
          body:JSON.stringify(draft)
        });
      }
      // after save, refresh current view
      if (searchQuery && searchQuery.trim()) {
        await fetchAllReleases();
      } else {
        await fetchMonthReleases();
      }
      setShowReleaseModal(false);
    }catch(err){
      console.error('save release error:',err);
      alert(err.message);
    }
  };

  const handleDateFilter     = ()=> setIsFilterApplied(true);
  const resetFilter          = ()=>{ setFromDate(''); setToDate(''); setIsFilterApplied(false); };

  /* month navigation */
  const prevMonth = () => setSelectedMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setSelectedMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const goToToday = () => {
    const now = new Date();
    setSelectedMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  /* ─ derived list – applies to loaded month data ─ */
  const sorted = useMemo(() =>
    [...releases].sort((a,b)=>new Date(a.releaseDate)-new Date(b.releaseDate)),
    [releases]
  );
  let visible  = sorted.filter(r=> !r.isArchived);

  // Search filter (by track name or artist name)
  if (searchQuery && searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    visible = visible.filter(r => {
      // Search in track title
      const matchesTitle = r.title?.toLowerCase().includes(query);
      
      // Search in artist names
      const arr = Array.isArray(r.artists) ? r.artists : [];
      const artistNames = arr
        .map((a) => {
          if (a && typeof a === 'object') return a.name || a.title || '';
          const id = String(a || '');
          return artistNameMap[id] || '';
        })
        .filter(Boolean)
        .join(' ');
      const matchesArtist = artistNames.toLowerCase().includes(query);
      
      return matchesTitle || matchesArtist;
    });
  }

  if (isFilterApplied && (fromDate||toDate)) {
    visible = visible.filter(r=>{
      if (!r.releaseDate) return false;
      const d = new Date(r.releaseDate);
      const f = fromDate ? new Date(fromDate) : null;
      const t = toDate   ? new Date(toDate)   : null;
      return (!f || d>=f) && (!t || d<=t);
    });
  }

  /* ─ count releases in current month (before search filter) ─ */
  const monthCount = useMemo(() => {
    // If searching, count releases in the selected month from all loaded data
    if (searchQuery && searchQuery.trim()) {
      return sorted.filter(r => {
        if (r.isArchived) return false;
        if (!r.releaseDate) return false;
        const releaseDate = new Date(r.releaseDate);
        const releaseMonth = releaseDate.getMonth();
        const releaseYear = releaseDate.getFullYear();
        return releaseMonth === selectedMonth.getMonth() && releaseYear === selectedMonth.getFullYear();
      }).length;
    }
    // If not searching, we already have month-specific data
    return sorted.filter(r => !r.isArchived).length;
  }, [sorted, selectedMonth, searchQuery]);

  /* ─ organize releases by date for calendar view (with project grouping) ─ */
  const releasesByDate = useMemo(() => {
    const days = {};
    
    // Group releases by date
    visible.forEach(rel => {
      if (!rel.releaseDate) return;
      const key = rel.releaseDate.split('T')[0];
      if (!days[key]) days[key] = [];
      days[key].push({ ...rel, itemType: 'release' });
    });
    
    // Add projects that release on the same dates and reorganize
    Object.keys(days).forEach(key => {
      const releases = days[key];
      const projectIds = new Set();
      const projectsToAdd = [];
      
      // Find all unique projects for this date (only EP/Album)
      releases.forEach(rel => {
        if (rel.projectId) {
          const projId = String(rel.projectId);
          if (!projectIds.has(projId)) {
            projectIds.add(projId);
            const proj = allProjects.find(p => String(p._id) === projId);
            // Only show EP or Album projects
            const projType = (proj?.projectType || '').toLowerCase();
            const isAlbumOrEP = projType === 'ep' || projType === 'album';
            if (proj && proj.releaseDate && proj.releaseDate.split('T')[0] === key && isAlbumOrEP) {
              projectsToAdd.push({ ...proj, itemType: 'project' });
            }
          }
        }
      });
      
      // Reorganize: project → its releases (indented) → other releases
      const organized = [];
      projectsToAdd.forEach(proj => {
        organized.push(proj);
        releases
          .filter(r => String(r.projectId) === String(proj._id))
          .forEach(r => organized.push({ ...r, indent: true }));
      });
      
      // Add standalone releases (no project or project not releasing same day or not EP/Album)
      releases
        .filter(r => !projectsToAdd.some(proj => String(proj._id) === String(r.projectId)))
        .forEach(r => organized.push(r));
      
      days[key] = organized;
    });
    
    return days;
  }, [visible, allProjects]);
  
  /* ─ group releases by project for list view ─ */
  const groupedReleases = useMemo(() => {
    const items = [];
    
    // Group releases by date first
    const byDate = {};
    visible.forEach(rel => {
      const dateKey = rel.releaseDate ? rel.releaseDate.split('T')[0] : 'no-date';
      if (!byDate[dateKey]) byDate[dateKey] = [];
      byDate[dateKey].push(rel);
    });
    
    // Process each date
    Object.entries(byDate).forEach(([dateKey, releases]) => {
      const projectsOnThisDate = new Set();
      const projectsToShow = [];
      
      // Find projects that release on this specific date (only EP/Album)
      releases.forEach(rel => {
        if (rel.projectId) {
          const projId = String(rel.projectId);
          if (!projectsOnThisDate.has(projId)) {
            const projectInfo = allProjects.find(p => String(p._id) === projId);
            // Only show EP or Album projects with a release date matching this date
            if (projectInfo && projectInfo.releaseDate && projectInfo.releaseDate.split('T')[0] === dateKey) {
              const projType = (projectInfo.projectType || '').toLowerCase();
              const isAlbumOrEP = projType === 'ep' || projType === 'album';
              if (isAlbumOrEP) {
                projectsOnThisDate.add(projId);
                projectsToShow.push({
                  projectId: projId,
                  projectInfo: projectInfo,
                });
              }
            }
          }
        }
      });
      
      // Add project headers and their releases
      projectsToShow.forEach(({ projectId, projectInfo }) => {
        // Add project header
        items.push({
          type: 'project-header',
          projectId: projectId,
          projectInfo: projectInfo,
          releases: releases.filter(r => String(r.projectId) === projectId),
          key: `proj-header-${projectId}-${dateKey}`,
        });
        
        // Add releases for this project (indented)
        releases
          .filter(r => String(r.projectId) === projectId)
          .forEach(rel => {
            items.push({
              type: 'release',
              data: rel,
              indent: true,
              projectName: projectInfo?.title,
              key: `release-${rel._id}`,
            });
          });
      });
      
      // Add standalone releases (no project OR project not releasing on this date OR not EP/Album)
      releases
        .filter(r => !projectsToShow.some(p => p.projectId === String(r.projectId)))
        .forEach(rel => {
          items.push({
            type: 'release',
            data: rel,
            indent: false,
            key: `release-${rel._id}`,
          });
        });
    });
    
    return items;
  }, [visible, allProjects]);

  /* ─ render ─ */
  return (
    <div style={{ padding: 20 }}>
      {/* Header with Share button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px' }}>
        <h1
          className="header"
          style={{ margin: '10px 0', color: '#fff' }}
        >
          {isPublicView ? 'CloudKid Release Overview' : 'Release Overview'}
        </h1>

        {!isPublicView && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={handleShare}
              style={{
                padding: '10px 20px',
                background: '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#32CD32'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#28a745'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"></circle>
                <circle cx="6" cy="12" r="3"></circle>
                <circle cx="18" cy="19" r="3"></circle>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
              </svg>
              Share
            </button>
            
            {showShareCopied && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 8,
                  padding: '8px 12px',
                  background: '#2a2a2a',
                  color: '#7eef4f',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                }}
              >
                ✓ Link copied!
              </div>
            )}
          </div>
        )}
        </div>

      {/* Search + Filters Row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          marginBottom: '24px',
          gap: '15px',
          flexWrap: 'wrap',
        }}
      >
        {/* Search Bar + View Toggle Container */}
        <div style={{ marginLeft: '8px', flexShrink: 0 }}>
          {/* Search Bar + View Toggle Row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Search Bar */}
            <div style={{ maxWidth: '300px', width: '300px', position: 'relative' }}>
              <svg
                style={{
                  position: 'absolute',
                  left: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '18px',
                  height: '18px',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
                fill="none"
                stroke="#757575"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search tracks or artists..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 20px 14px 42px',
                  fontSize: '1rem',
                  backgroundColor: '#2a2a2a',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  outline: 'none',
                  transition: 'none',
                  boxShadow: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            
            {/* View Toggle */}
            <div style={{ display: 'flex', gap: '4px', background: '#2a2a2a', padding: '4px', borderRadius: '8px' }}>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '8px 12px',
                background: viewMode === 'list' ? '#555' : 'transparent',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                transition: 'background 0.2s',
              }}
              title="List view"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6"></line>
                <line x1="8" y1="12" x2="21" y2="12"></line>
                <line x1="8" y1="18" x2="21" y2="18"></line>
                <line x1="3" y1="6" x2="3.01" y2="6"></line>
                <line x1="3" y1="12" x2="3.01" y2="12"></line>
                <line x1="3" y1="18" x2="3.01" y2="18"></line>
              </svg>
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              style={{
                padding: '8px 12px',
                background: viewMode === 'calendar' ? '#555' : 'transparent',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                transition: 'background 0.2s',
              }}
              title="Calendar view"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </button>
            </div>
          </div>
          
          {/* Release Counts Row */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
          <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: '#2a2a2a',
              padding: '6px 12px',
              borderRadius: 8,
              fontSize: 13,
              color: '#ddd',
              fontWeight: 500,
            }}>
              <span style={{ color: '#7eef4f', marginRight: 6, fontWeight: 600 }}>
                {monthCount}
              </span>
              <span style={{ color: '#999' }}>
                {monthCount === 1 ? 'release' : 'releases'} this month
              </span>
            </div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: '#2a2a2a',
              padding: '6px 12px',
              borderRadius: 8,
              fontSize: 13,
              color: '#ddd',
              fontWeight: 500,
            }}>
              <span style={{ color: '#7eef4f', marginRight: 6, fontWeight: 600 }}>
                {loadingYearCount ? '...' : yearReleasesCount}
              </span>
              <span style={{ color: '#999' }}>
                {loadingYearCount ? '' : `${yearReleasesCount === 1 ? 'release' : 'releases'} in ${selectedMonth.getFullYear()}`}
              </span>
          </div>
          </div>
        </div>

        {/* Filter Section */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            flexWrap: 'wrap',
            flex: 1,
            justifyContent: 'flex-end',
          }}
        >
        {/* Month Navigation */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            backgroundColor: '#1e1e1e',
            padding: '10px 16px',
            borderRadius: '8px',
          }}
        >
          <button
            onClick={prevMonth}
            style={{
              background: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              width: 32,
              height: 32,
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: '32px',
            }}
          >
            ‹
        </button>
          <span style={{ color: '#fff', fontSize: '0.95rem', fontWeight: 600, minWidth: '140px', textAlign: 'center' }}>
            {MONTHS[selectedMonth.getMonth()]} {selectedMonth.getFullYear()}
          </span>
          <button
            onClick={nextMonth}
            style={{
              background: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              width: 32,
              height: 32,
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: '32px',
            }}
          >
            ›
          </button>
          <button
            onClick={goToToday}
            style={{
              background: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              height: 32,
              padding: '0 10px',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
            title="Jump to current month"
          >
            Today
        </button>
        </div>
        </div>
      </div>

      {/* error */}
      {error && <div style={{ color:'salmon', marginBottom:12 }}>{error}</div>}

      {/* Grid View */}
      {viewMode === 'grid' && (
        <>
      <div style={{ display:'flex',flexWrap:'wrap',alignItems:'flex-start' }}>
            {/* add card - hide in public view */}
            {!isPublicView && (
        <div onClick={handleAddRelease} style={{
              width:260,height:260,padding:20,margin:8,display:'flex',
              justifyContent:'center',background:'#1e1e1e',borderRadius:10,
              cursor:'pointer',color:'#555',fontSize:'3rem',alignItems:'center'
            }}>
          +
        </div>
            )}

        {/* existing releases (loaded subset) */}
        {visible.map(rel => (
          <div key={rel._id} style={{ position: 'relative' }}>
            <ReleaseCard
              release={rel}
              plannedBudget={releaseBudgets[rel._id] || 0}
              artistNameMap={artistNameMap}
              isPublicView={isPublicView}
            />
          </div>
        ))}
      </div>
        </>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <>
          <div style={{ marginBottom: '20px' }}>
            {/* Add button - hide in public view */}
            {!isPublicView && (
              <button
                onClick={handleAddRelease}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  background: '#2a2a2a',
                  border: 'none',
                  borderRadius: 8,
                  color: '#ddd',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  fontWeight: 500,
                  marginBottom: '16px',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#333'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#2a2a2a'}
              >
                <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>+</span>
                Add Track
              </button>
            )}

            {/* List items - With project grouping */}
            {groupedReleases.map(item => {
                if (item.type === 'project-header') {
                  // Project header - visual only
                  const proj = item.projectInfo;
                  if (!proj) {
                    // If no project info, just show a simple divider
                    return (
                      <div
                        key={item.key}
                        style={{
                          padding: '10px 20px',
                          background: '#252525',
                          borderRadius: 8,
                          marginBottom: '8px',
                          borderLeft: '4px solid #7eef4f',
                        }}
                      >
                        <div style={{ fontSize: '0.85rem', color: '#999', fontWeight: 600 }}>
                          Project Release ({item.releases.length} {item.releases.length === 1 ? 'track' : 'tracks'})
                        </div>
                      </div>
                    );
                  }
                  
                  const arr = Array.isArray(proj.artists) ? proj.artists : [];
                  const displayArtist = arr
                    .map((a) => {
                      if (a && typeof a === 'object') return a.name || a.title || '';
                      const id = String(a || '');
                      return artistNameMap[id] || '';
                    })
                    .filter(Boolean)
                    .join(', ');
                  
                  return (
                    <div
                      key={item.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        background: '#252525',
                        borderRadius: 8,
                        marginBottom: '8px',
                        gap: '16px',
                        borderLeft: '4px solid #7eef4f',
                      }}
                    >
                      {/* Cover Art */}
                      <img
                        src={proj.coverArt || emptyArt}
                        alt="Cover"
                        loading="lazy"
                        style={{
                          width: 50,
                          height: 50,
                          objectFit: 'cover',
                          borderRadius: 6,
                          flexShrink: 0,
                        }}
                      />
                      
                      {/* Project Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                          <h3
                            style={{
                              margin: 0,
                              fontSize: '0.95rem',
                              fontWeight: 600,
                              color: '#7eef4f',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {proj.title}
                          </h3>
                          <span
                            style={{
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              padding: '2px 6px',
                              background: '#7eef4f',
                              color: '#000',
                              borderRadius: 3,
                              flexShrink: 0,
                              textTransform: 'uppercase',
                            }}
                          >
                            {proj.projectType || 'EP'}
                          </span>
                        </div>
                        <p
                          style={{
                            margin: 0,
                            fontSize: '0.85rem',
                            color: '#bbb',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {displayArtist || 'No Artist'}
                        </p>
                      </div>
                    </div>
                  );
                }
                
                // Release row
                const rel = item.data;
              
                const formatted = rel.releaseDate
                ? new Date(rel.releaseDate)
                    .toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                    })
                    .replaceAll('/', '.')
                : '—';

              const arr = Array.isArray(rel.artists) ? rel.artists : [];
              const displayArtist = arr
                .map((a) => {
                  if (a && typeof a === 'object') return a.name || a.title || '';
                  const id = String(a || '');
                  return artistNameMap[id] || '';
                })
                .filter(Boolean)
                .join(', ');

              const hasBudget = Number(releaseBudgets[rel._id] || 0) > 0;
              const hasAudio = rel.uploads?.mp3Master;
              const isCurrentlyPlaying = currentTrack?.releaseId === rel._id && globalIsPlaying;

              const listItemContent = (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '16px 20px',
                      background: '#1e1e1e',
                      borderRadius: 10,
                      marginBottom: '8px',
                      marginLeft: item.indent ? '16px' : '0',
                      opacity: rel.isArchived ? 0.5 : 1,
                      transition: 'background 0.2s',
                      cursor: isPublicView ? 'default' : 'pointer',
                      gap: '20px',
                      borderLeft: item.indent ? '3px solid #555' : 'none',
                    }}
                    onMouseEnter={(e) => !isPublicView && (e.currentTarget.style.background = '#252525')}
                    onMouseLeave={(e) => !isPublicView && (e.currentTarget.style.background = '#1e1e1e')}
                  >
                    {/* Cover Art with Play Button */}
                    <div
                      style={{
                        position: 'relative',
                        width: 60,
                        height: 60,
                        flexShrink: 0,
                        cursor: hasAudio ? 'pointer' : 'default',
                      }}
                      onClick={(e) => {
                        if (hasAudio) {
                          e.preventDefault();
                          e.stopPropagation();
                          handlePlayAudio(rel);
                        }
                      }}
                    >
                      <img
                        src={rel.coverArt || emptyArt}
                        alt="Cover"
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: 8,
                        }}
                      />
                      {hasAudio && (
                        <div
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = 0.9;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = isCurrentlyPlaying ? 0.9 : 0;
                          }}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(0, 0, 0, 0.6)',
                            borderRadius: 8,
                            opacity: isCurrentlyPlaying ? 0.9 : 0,
                            transition: 'opacity 0.2s',
                            cursor: 'pointer',
                            zIndex: 1,
                          }}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="white"
                            style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }}
                          >
                            {isCurrentlyPlaying ? (
                              // Pause icon
                              <>
                                <rect x="6" y="4" width="4" height="16" />
                                <rect x="14" y="4" width="4" height="16" />
                              </>
                            ) : (
                              // Play icon
                              <path d="M8 5v14l11-7z" />
                            )}
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Track Info */}
                    <div style={{ flex: '1 1 250px', minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        {hasBudget && !isPublicView && (
                          <img
                            src={dollarIcon}
                            alt="Budget set"
                            style={{
                              width: 16,
                              height: 16,
                              filter:
                                'invert(68%) sepia(51%) saturate(734%) hue-rotate(63deg) brightness(97%) contrast(96%)',
                            }}
                          />
                        )}
                        <h3
                          style={{
                            margin: 0,
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: '#fff',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {rel.title}
                        </h3>
                      </div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: '0.9rem',
                          color: '#ccc',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {displayArtist}
                      </p>
                    </div>

                    {/* Release Date */}
                    <div
                      style={{
                        flex: '0 0 90px',
                        color: '#aaa',
                        fontSize: '0.85rem',
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: 2 }}>Date</div>
                      <div>{formatted}</div>
                    </div>

                    {/* Release Type */}
                    <div
                      style={{
                        flex: '0 0 100px',
                        color: '#aaa',
                        fontSize: '0.85rem',
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: 2 }}>Type</div>
                      <div>{rel.releaseType || '—'}</div>
                    </div>

                    {/* ISRC */}
                    <div
                      style={{
                        flex: '0 0 120px',
                        color: '#aaa',
                        fontSize: '0.85rem',
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: 2 }}>ISRC</div>
                      <div style={{ fontFamily: 'monospace', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <span>{rel.isrc || '—'}</span>
                        {rel.isrc && rel.isrc !== '—' && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              copyToClipboard(rel.isrc, 'ISRC');
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                            title="Copy ISRC"
                          >
                            <img
                              src={duplicateIcon}
                              alt="Copy"
                              style={{ width: 14, height: 14, filter: 'invert(70%)', opacity: 1 }}
                            />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* UPC */}
                    <div
                      style={{
                        flex: '0 0 120px',
                        color: '#aaa',
                        fontSize: '0.85rem',
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: 2 }}>UPC</div>
                      <div style={{ fontFamily: 'monospace', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <span>{rel.trackUpc || rel.upc || '—'}</span>
                        {(rel.trackUpc || rel.upc) && (rel.trackUpc || rel.upc) !== '—' && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              copyToClipboard(rel.trackUpc || rel.upc, 'UPC');
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                            title="Copy UPC"
                          >
                            <img
                              src={duplicateIcon}
                              alt="Copy"
                              style={{ width: 14, height: 14, filter: 'invert(70%)', opacity: 1 }}
                            />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* All Info Button */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openInfoFor(rel._id);
                      }}
                      style={{
                        flex: '0 0 auto',
                        padding: '6px 12px',
                        background: '#333',
                        color: '#ddd',
                        border: 'none',
                        borderRadius: 6,
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                        e.currentTarget.style.background = '#444';
                      }}
                      onMouseLeave={(e) => {
                        e.stopPropagation();
                        e.currentTarget.style.background = '#333';
                      }}
                    >
                      All Info
                    </button>
                  </div>
              );
              
              return isPublicView ? (
                <div key={rel._id} style={{ textDecoration: 'none', color: 'inherit' }}>
                  {listItemContent}
                </div>
              ) : (
                <Link
                  key={rel._id}
                  to={`/release/${rel._id}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  {listItemContent}
                </Link>
              );
            })
            .filter(Boolean)}
          </div>
        </>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <>
          {/* Calendar Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 1,
              background: '#2a2a2a',
            }}
          >
            {/* Weekday headers */}
            {WEEKDAYS.map((day) => (
              <div
                key={day}
                style={{
                  background: '#1e1e1e',
                  padding: 10,
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: 14,
                  color: '#999',
                }}
              >
                {day}
              </div>
            ))}

            {/* Calendar cells */}
            {(() => {
              const y = selectedMonth.getFullYear();
              const m = selectedMonth.getMonth();
              const totalDays = dim(y, m);
              const startDay = first(y, m);
              const cells = [];
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              // Add empty cells for days before month starts
              for (let i = 0; i < startDay; i++) {
                cells.push({ date: null, key: `empty-${i}`, inMonth: false });
              }

              // Add cells for each day in the month
              for (let d = 1; d <= totalDays; d++) {
                const date = new Date(y, m, d);
                const key = ymd(date);
                cells.push({ date, key, inMonth: true });
              }

              return cells.map(({ date, key, inMonth }) => {
                const list = releasesByDate[key] || [];
                const isToday = date && date.getTime() === today.getTime();

                return (
                  <div
                    key={key}
                    style={{
                      background: inMonth ? '#1e1e1e' : '#242424',
                      minHeight: 120,
                      padding: '6px',
                      position: 'relative',
                      border: isToday ? '2px solid #7eef4f' : '1px solid #2a2a2a',
                      overflowY: 'auto',
                    }}
                  >
                    {/* Day number */}
                    {date && (
                      <span
                        style={{
                          position: 'absolute',
                          top: 4,
                          left: 6,
                          fontSize: 11,
                          fontWeight: 600,
                          color: inMonth ? '#999' : '#555',
                        }}
                      >
                        {date.getDate()}
                      </span>
                    )}

                    {/* Items for this day */}
                    <div style={{ marginTop: 20 }}>
                      {list.map((item) => {
                        const arr = Array.isArray(item.artists) ? item.artists : [];
                        const artistName = arr
                          .map((a) => {
                            if (a && typeof a === 'object') return a.name || a.title || '';
                            const id = String(a || '');
                            return artistNameMap[id] || '';
                          })
                          .filter(Boolean)
                          .join(', ');

                        return (
                          <div
                            key={item._id}
                            style={{
                              position: 'relative',
                              display: 'flex',
                              gap: 6,
                              alignItems: 'center',
                              background: '#2d2d2d',
                              borderRadius: 4,
                              padding: 10,
                              marginTop: item.indent ? 6 : 8,
                              marginLeft: item.indent ? 4 : 0,
                              borderLeft: item.indent ? '3px solid #555' : 'none',
                              cursor: isPublicView ? 'default' : 'pointer',
                            }}
                            onClick={isPublicView ? undefined : () => {
                              if (item.itemType === 'project') {
                                window.location.href = `/project/${item._id}`;
                              } else {
                                window.location.href = `/release/${item._id}`;
                              }
                            }}
                          >
                            {/* Info button - only for releases */}
                            {item.itemType === 'release' && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openInfoFor(item._id);
                                }}
                                title="Release info"
                                style={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  width: 19,
                                  height: 21,
                                  background: 'transparent',
                                  border: 'none',
                                  padding: 0,
                                  cursor: 'pointer',
                                }}
                              >
                                <img
                                  src={infoIcon}
                                  alt="Info"
                                  style={{
                                    width: 18,
                                    height: 18,
                                    filter: 'invert(70%)',
                                    opacity: 1,
                                    display: 'block',
                                  }}
                                />
                              </button>
                            )}

                            {/* Cover art with play button */}
                            <div
                              style={{
                                position: 'relative',
                                width: 40,
                                height: 40,
                                flexShrink: 0,
                              }}
                            >
                              <img
                                src={item.coverArt || emptyArt}
                                alt=""
                                loading="lazy"
                                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, display: 'block' }}
                              />
                              
                              {/* Play button overlay - only for releases with audio */}
                              {item.itemType === 'release' && item.uploads?.mp3Master && (
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePlayAudio(item);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'rgba(0, 0, 0, 0.6)',
                                    borderRadius: 4,
                                    opacity: (currentTrack?.releaseId === item._id && globalIsPlaying) ? 0.9 : 0,
                                    transition: 'opacity 0.2s',
                                    cursor: 'pointer',
                                    zIndex: 1,
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.opacity = '0.9';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!(currentTrack?.releaseId === item._id && globalIsPlaying)) {
                                      e.currentTarget.style.opacity = '0';
                                    }
                                  }}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="white"
                                    style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }}
                                  >
                                    {(currentTrack?.releaseId === item._id && globalIsPlaying) ? (
                                      // Pause icon
                                      <>
                                        <rect x="6" y="4" width="4" height="16" />
                                        <rect x="14" y="4" width="4" height="16" />
                                      </>
                                    ) : (
                                      // Play icon
                                      <path d="M8 5v14l11-7z" />
                                    )}
                                  </svg>
                                </div>
                              )}
                            </div>

                            {/* Item info */}
                            <div
                              style={{
                                fontSize: 12,
                                lineHeight: 1.2,
                                color: '#ddd',
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0,
                                overflow: 'hidden',
                                flex: 1,
                              }}
                            >
                              {/* Title and artist */}
                              <div style={{ paddingRight: 24, minWidth: 0 }}>
                                <strong
                                  style={{
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    display: 'block',
                                  }}
                                >
                                  {item.title}
                                </strong>
                                <span
                                  style={{
                                    color: '#999',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    display: 'block',
                                  }}
                                >
                                  {artistName}
                                </span>
                              </div>

                              {/* Type tag */}
                              {(() => {
                                const rawTag = item.itemType === 'project' ? item.projectType : item.releaseType;
                                const tag = normalizeTag(rawTag);
                                const tagColor = TAG_COLORS[tag] || '#555';
                                
                                if (!tag) return null;
                                
                                return (
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: 4,
                                      marginTop: 2,
                                      flexWrap: 'nowrap',
                                      overflow: 'hidden',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 'bold',
                                        background: tagColor,
                                        padding: '2px 4px',
                                        borderRadius: 4,
                                        color: tag === 'Focus Single' ? '#111' : '#fff',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        minWidth: 0,
                                        flex: '0 1 auto',
                                        maxWidth: '100%',
                                      }}
                                    >
                                      {tag}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </>
      )}

      {/* shared modal */}
      {showReleaseModal && (
  <ReleaseModal
    open={showReleaseModal}
    initialRelease={editingDraft}
    allArtists={allArtists}
    allProjects={allProjects}
    onArtistsRefresh={fetchArtists}
    onSave={saveRelease}
    onCancel={() => setShowReleaseModal(false)}
  />
)}

      {/* Release Infocard */}
      <ReleaseInfocard
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        release={infoRelease}
      />
    </div>
  );
};

export default ReleaseOverview;